// src/apis/HaciendaApi.ts
import { TIPOS_DE_IDENTIFICACION } from "@utils/Constants";
import axios, { AxiosInstance } from "axios";

export interface TipoCambioData {
    venta: {
        fecha: string; // ISO string con zona horaria de CR
        valor: number;
    };
    compra: {
        fecha: string;
        valor: number;
    };
}
export interface AERegimen {
    codigo: 0 | 1 | 2;//  0 = No aplica |  1 = Régimen tradicional | 2 = Régimen simplificado
    descripcion: string;
}

export interface AESituacion {
    moroso: "SI" | "NO";
    omiso: "SI" | "NO";
    estado: string; // p.ej. "Inscrito"
    administracionTributaria: string; // p.ej. "San Jose"
}

export interface AEActividad {
    estado: "A" | "I";      // p.ej. A = Activa | I = Inactiva
    tipo: "P" | "S";        // p.ej. "P" => primary ||  S = Secundaria
    codigo: number;      // p.ej. 751101
    descripcion: string; // p.ej. "ACTIVIDADES..."
}

export interface ActividadEconomicasResponse {
    nombre: string;
    tipoIdentificacion: keyof typeof TIPOS_DE_IDENTIFICACION;
    regimen: AERegimen;
    situacion: AESituacion;
    actividades: AEActividad[];
}

export interface CabysItem {
    codigo: string;            // 13 dígitos
    descripcion: string;
    categorias: string[];
    impuesto: number;          // 0, 1, 2, 4, 13, etc.
    uri?: string;              // solo viene en la variante de búsqueda por q
}

export interface CabysSearchResponse {
    total: number;             // total de coincidencias en el catálogo
    cantidad: number;          // cantidad devuelta en esta respuesta
    cabys: CabysItem[];
}

/**
 * Error de conexión/transitorio contra Hacienda (timeout, red, CORS/redirect 307
 * del balanceador de carga, o 5xx). Se distingue de un 404 "no encontrado" real
 * para que la UI pueda mostrar un mensaje adecuado y sugerir reintentar.
 */
export class HaciendaConnectionError extends Error {
    constructor(message = "No se pudo conectar con Hacienda. Intente de nuevo en unos segundos.") {
        super(message);
        this.name = "HaciendaConnectionError";
    }
}

/**
 * Hacienda respondio que la identificacion no existe o no esta inscrita (404).
 *
 * Se distingue de `HaciendaConnectionError` porque son respuestas opuestas: esta es
 * definitiva y justifica bloquear un dato, mientras que la de conexion solo dice que no se
 * pudo averiguar. Quien valide contra el padron necesita separar las dos sin comparar texto.
 */
export class HaciendaNotRegisteredError extends Error {
    constructor(message = "Identificación no encontrada o no inscrita en Hacienda (404).") {
        super(message);
        this.name = "HaciendaNotRegisteredError";
    }
}

export class Api {
    private client: AxiosInstance;

    constructor(baseURL = "https://api.hacienda.go.cr") {
        this.client = axios.create({
            baseURL,
            timeout: 10000, // 10 segundos: la consulta de AE puede ser lenta
        });
    }

    /**
     * Reintenta `fn` ante fallos transitorios. El balanceador de carga de Hacienda
     * responde esporádicamente con un 307 (para fijar su cookie de persistencia) que,
     * al ser cross-origin sin headers CORS, el navegador aborta como error de red.
     * Ese fallo no expone `response`, por lo que reintentamos cuando no hay `response`
     * o el status es 5xx. Un 404 (no inscrito) NO se reintenta.
     */
    private async withRetry<T>(fn: () => Promise<T>, retries = 2, delayMs = 400): Promise<T> {
        let lastErr: unknown;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await fn();
            } catch (err: any) {
                lastErr = err;
                const status = err?.response?.status;
                const isTransient = !err?.response || status >= 500;
                if (!isTransient || attempt === retries) throw err;
                await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
            }
        }
        throw lastErr;
    }

    /**
     * Obtiene el tipo de cambio del dólar (compra y venta)
     */
    async getTipoCambioDolar(): Promise<TipoCambioData> {
        try {
            const response = await this.withRetry(() =>
                this.client.get<TipoCambioData>("/indicadores/tc/dolar")
            );
            return response.data;
        } catch (error: any) {
            console.error("Error al obtener tipo de cambio del dólar:", error.message || error);
            throw error;
        }
    }

    /**
   * Consulta de Información de Actividades Económicas (AE) por número de identificación.
   * @param identificacion Cédula/Jurídica/Dimex, con o sin ceros a la izquierda.
   */
    async getActividadesEconomicas(
        identificacion: string | number
    ): Promise<ActividadEconomicasResponse> {
        const id = String(identificacion).trim();
        try {
            const { data } = await this.withRetry(() =>
                this.client.get<ActividadEconomicasResponse>("/fe/ae", {
                    params: { identificacion: id },
                })
            );
            return data;
        } catch (error: any) {
            // 404: no inscrito o no existe → deja que el caller decida qué hacer
            if (error?.response?.status === 404) {
                throw new HaciendaNotRegisteredError();
            }
            // Sin response (timeout/red/CORS/307) o 5xx tras agotar reintentos → transitorio
            console.error("Error obtener una respuesta de Hacienda:", error?.message || error);
            throw new HaciendaConnectionError();
        }
    }

    /**
   * CABYS: búsqueda por texto.
   * @param q término de búsqueda (p. ej. "jugo de tomate")
   * @param top máximo de resultados (opcional, por defecto 10)
   */
    async searchCabys(q: string, top?: number): Promise<CabysSearchResponse> {
        if (!q?.trim()) throw new Error("Por favor ingrese un filtro de busqueda");
        const { data } = await this.withRetry(() =>
            this.client.get<CabysSearchResponse>("/fe/cabys", {
                params: { q, top },
            })
        );
        return data;
    }

    /**
     * CABYS: búsqueda por código exacto (13 dígitos).
     * Hacienda devuelve un arreglo (normalmente 1 ítem si existe).
     */
    async getCabysByCodigo(codigo: string | number): Promise<CabysItem[]> {
        const code = String(codigo).trim();
        if (!/^\d{13}$/.test(code)) {
            console.warn("getCabysByCodigo: el código debería tener 13 dígitos.");
        }
        const { data } = await this.withRetry(() =>
            this.client.get<CabysItem[]>("/fe/cabys", {
                params: { codigo: code },
            })
        );
        return data;
    }
}

export const HaciendaApi = new Api()